import { RouterViewQuery } from './router-view-query';
import {
  addUrlChangeListener,
  match as getMatch,
  removeUrlChangeListener,
  URLChangeListenerFunction,
  URLInitOptions,
  URLMatchResult,
} from '../../core/routing';
import { SW_QUERY_UPDATE_EVENT, SW_ROUTER_VIEW_AVAILABLE_ATTRIBUTES } from './router-view.config';
import { defineElement } from '../../core/routing.util';

/**
 * Immutable reference to a failed match.
 *
 * @private
 * @ignore
 */
const IMMUTABLE_FALSE = Object.freeze({ isMatch: false });

/**
 * `RouterView`: a NativeJS Web Component for creating self-updating views which interface with the
 * main routing library functions.
 *
 * Every instance of this component will register itself with the routing library, and listen to
 * URLChangeEvents. Every time the URL change, the component's match is matched against the page's
 * new URL. If the match is successful, the component will become visible (or remain visible), and
 * if it fails, the component will become invisible (or remain invisible).
 *
 * The component's `match` attribute is passed as received onto the routing library. Check the routing
 * library's `match` function for documentation on the matching string syntax. Nonetheless, if you want to
 * match several queries you will need to add `RouterViewQuery` components inside your `RouterView`. In each
 * of these elements you will need to add `slot="query"` as an attribute for the `RouterView`to detect that you
 * want to perform more that one query. After that, you just need to pass the correct attributes(namespace, pathname, hash, search-params) for each new query.
 *
 * IMPORTANT: Bear in mind that `RouterView` will check for `RouterViewQuery` components before checking it's
 * own options. If `RouterViewQuery` components exist, it will not compute `RouterView` attributes
 * and query will not be matched.
 *
 * Single query example:
 *      <sw-router-view namespace='x' match='/foo'>
 *        <p>I'm inside the view</p>
 *      </sw-router-view>
 *
 * Multiple query example:
 *     <sw-router-view>
 *       <p>I'm inside the view</p>
 *       <sw-router-view-query slot="query" namespace="foo" pathname="/bar"></sw-router-view-query>
 *       <sw-router-view-query slot="query" namespace="oof" pathname="/dar"></sw-router-view-query>
 *       <sw-router-view-query slot="query" namespace="dalhe" pathname="/ze"></sw-router-view-query>
 *     </sw-router-view>
 *
 *
 * Additionally, every time the match result changes, the component instance will fire a `match-change`
 * CustomEvent, with the match information. So if you use this component like so:
 *
 *     <sw-router-view namespace='x' match='/foo'>
 *         <p>I'm inside the view, and I'm <span data-js-hook>invisible</span></p>
 *     </sw-router-view>
 *
 * You can listen to that event via the normal DOM APIs
 *
 *     document.querySelector('sw-router-view').addEventListener('match-change', (matchChangeEvent) => {
 *       const matchDetail = matchChangeEvent.detail;
 *       const span = matchChangeEvent.querySelector('[data-js-hook]);
 *       if (matchDetail.isMatch) {
 *         span.textContent = 'visible';
 *       } else {
 *         span.textContent = 'invisible';
 *       }
 *     })
 *
 * The detail of the `'match-change'` CustomEvent is a frozen version of the return of the routing
 * library's `match`'s return. Check the documentation of that function for more details on the object.
 *
 * @export
 * @class RouterView
 * @extends {HTMLElement}
 */
export class RouterView extends HTMLElement {
  /**
   * The dynamic attributes supported by the `RouterView` web component.
   * - `namespace` - required, the namespace of the match you're checking, if absent the view will not be visible
   * - `match` - required, how to match the URL, if absent the view will not be visible
   *
   * @readonly
   * @static
   * @type {string[]}
   * @memberof RouterView
   */
  static get observedAttributes(): string[] {
    return SW_ROUTER_VIEW_AVAILABLE_ATTRIBUTES;
  }

  /**
   * The handler function that is registered/unregistered on component connect/disconnect.
   *
   * @ignore
   * @private
   * @type {URLChangeListenerFunction}
   * @memberof RouterView
   */
  private onUrlChange: URLChangeListenerFunction;
  /**
   * The frozen version of the last return of the routing library's `match` call.
   *
   * @ignore
   * @private
   * @type {URLMatchResult}
   * @memberof RouterView
   */
  private currentMatch: URLMatchResult;

  /**
   * The slot for `<sw-router-view-query>` elements
   * @ignore
   * @private
   * @type {HTMLSlotElement}
   * @memberof RouterView
   */
  private querySlotElement: HTMLSlotElement;

  /**
   * The in-memory holder of unique URLInitOptions query for each `<sw-router-view-query>` element
   * @ignore
   * @private
   * @type {Map<symbol, URLInitOptions>}
   * @memberof RouterView
   */
  private matchQueries: Map<symbol, URLInitOptions>;

  /**
   * Boolean value that tells if there are `<ws-router-view-query>` elements inside RouterView
   */
  private hasQueryElements = false;

  /** @ignore */
  constructor() {
    super();
    this.onUrlChange = () => this.updateVisibility();
    this.currentMatch = IMMUTABLE_FALSE;
    this.matchQueries = new Map();

    const shadowRoot = this.attachShadow({ mode: 'open' });
    const slot = document.createElement('slot');
    shadowRoot.append(slot);

    // query slot element
    this.querySlotElement = document.createElement('slot');
    this.querySlotElement.setAttribute('name', 'query');
    shadowRoot.append(this.querySlotElement);

    this.addEventListener(SW_QUERY_UPDATE_EVENT, this.updateMatchQuery as EventListener);

    this.querySlotElement.addEventListener('slotchange', () => this.updateQuerySlots());
  }

  /**
   * Updates the matchQueries object when a `slotchange` event occurs.
   *
   * It will go through the RouterView slot element and check if the RouterViewQuery
   * elements inside of it match with the matchQueries object. If new elements are added
   * or removed, it updates the Map accordingly.
   *
   * After this, it will call the updateVisibility method to trigger the desired queries match.
   *
   * @ignore
   * @memberof RouterView
   */
  private updateQuerySlots(): void {
    const slotElements = this.querySlotElement.assignedElements();
    const queryElements = slotElements.filter((element) => element instanceof RouterViewQuery) as RouterViewQuery[];
    const queriesKeys = queryElements.map((queryElement) => queryElement.queryKey);

    if (queryElements.length > 0) {
      const validQueryEntries: [symbol, URLInitOptions][] = [];

      this.hasQueryElements = true;
      // Checks for removed query elements
      for (const [key, value] of this.matchQueries.entries()) {
        if (queriesKeys.includes(key)) {
          validQueryEntries.push([key, value]);
        }
      }

      // Checks for inserted query elements
      queryElements.forEach((queryElement) => {
        const isInQueriesArray = validQueryEntries.some((query) => query[0] === queryElement.queryKey);

        // if not in queries array, this query element is added
        if (!isInQueriesArray) {
          validQueryEntries.push([queryElement.queryKey, queryElement.queryOptions]);
        }
      });

      this.matchQueries = new Map(validQueryEntries);
    } else {
      this.hasQueryElements = false;
    }

    this.updateVisibility();
  }

  /**
   * Updates matchQueries for multiple queries when a `sw-query-update` event occurs.
   *
   * It will search for the key provided by `event.detail.key` inside the matchQueries
   * and will add/update the query options for that `<sw-router-view-query>` element.
   *
   *  Finally it will trigger updateVisibility to match the queries that present in
   * matchQueries
   *
   * @ignore
   * @param {CustomEvent} queryUpdateEvent The update event of the `<sw-router-view-query>`
   * @memberof RouterView
   */
  private updateMatchQuery(queryUpdateEvent: CustomEvent): void {
    queryUpdateEvent.stopPropagation();

    if (queryUpdateEvent.detail) {
      const { key, queryOptions } = queryUpdateEvent.detail;
      this.matchQueries.set(key, queryOptions);
    }

    this.updateVisibility();
  }

  /**
   * Accessor for the last result of the `match` call. If it hasn't been called yet
   * it will contain a failed match object.
   *
   * @readonly
   * @type {URLMatchResult}
   * @memberof RouterView
   */
  get match(): URLMatchResult {
    return this.currentMatch;
  }

  /**
   * Setter for match property.
   * @type {URLMatchResult}
   * @memberof RouterView
   */
  set match(newValue: URLMatchResult) {
    if (typeof newValue === 'string') {
      this.setAttribute('match', newValue);
    }
  }

  /**
   * The Web Component API function that is called every time this element instance is added onto
   * the DOM tree.
   *
   * We use this hook to add the URLChangeEventListener, since it doesn't make sense to update
   * the view if it is not present in the DOM.
   *
   * @ignore
   * @memberof RouterView
   */
  connectedCallback(): void {
    addUrlChangeListener(this.onUrlChange);
    this.updateVisibility();
  }

  /**
   * The Web Component API function that is called every time this element instance is added onto
   * the DOM tree.
   *
   * We use this hook to remove the URLChangeEventListener, since it doesn't make sense to update
   * the view if it is not present in the DOM.
   *
   * @ignore
   * @memberof RouterView
   */
  disconnectedCallback(): void {
    removeUrlChangeListener(this.onUrlChange);
  }

  /**
   * The Web Component API function that is called every time an attribute in the
   * `observedAttributes` array is modified from the outsides.
   *
   * It triggers a new call to `match`.
   *
   * @ignore
   * @memberof RouterView
   */
  attributeChangedCallback(): void {
    this.updateVisibility();
  }

  /**
   *
   * If there are no child query components:
   * * Checks the component's current attributes and calls the routing library's `match`
   * * function with them, triggering a `match-event` with the result.
   * *
   * * If any of the attributes are not set, `match` will not called but the method
   * * will behave as if a false result was returned.
   *
   * If there are child query components:
   * * Matches all the queries passed through the `matchQueries` array via the `router-view-query`
   * * child components.
   *
   * isMatch is always checked and passed accordingly for both cases to the correct match call
   *
   * @ignore
   * @private
   * @return {*}  {void}
   * @memberof RouterView
   */
  private updateVisibility() {
    let match;
    const isExact = this.isExact();
    if (this.hasQueryElements) {
      match = getMatch([...this.matchQueries.values()], { exact: isExact });
    } else {
      // checks router-view root attributes if there are no router-view-query elements
      const namespace = this.getAttribute('namespace');
      if (namespace == null || namespace.length === 0) {
        this.setVisible(false);
        this.updateMatch(IMMUTABLE_FALSE);
        return;
      }

      const matchString = this.getAttribute('match');

      if (matchString == null || matchString.length === 0) {
        this.setVisible(false);
        this.updateMatch(IMMUTABLE_FALSE);
        return;
      }

      match = getMatch(namespace, matchString, { exact: isExact });
    }

    this.setVisible(match.isMatch);
    this.updateMatch(match);
  }

  /**
   * Checks if the exact attribute is provided
   *
   * @ignore
   * @private
   * @return {*}  {boolean}
   */
  private isExact() {
    const exactAttribute = this.getAttribute('exact');
    return exactAttribute !== null;
  }

  /**
   * Hides or shows this component's element depending on the `isVisible` flag.
   *
   * @ignore
   * @private
   * @param {boolean} isVisible `true` to show the element, `false` to hide
   * @memberof RouterView
   */
  private setVisible(isVisible: boolean) {
    this.style.display = isVisible ? '' : 'none';
  }

  /**
   * Updates the internal, and accessible from the outside, match object with the a frozen version
   * of the given `newMatch` and triggers a CustomEvent with it.
   *
   * @ignore
   * @private
   * @param {URLMatchResult} newMatch
   * @memberof RouterView
   */
  private updateMatch(newMatch: URLMatchResult) {
    // Trigger the event if
    // 1. it has changed from true to false, or from false to true
    // 2. if it is true and was true previously, because the `variables` part may have changed
    if (newMatch.isMatch || newMatch.isMatch !== this.currentMatch.isMatch) {
      this.currentMatch = Object.freeze(newMatch);
      this.triggerMatchEvent(this.currentMatch);
    }
  }

  /**
   * Triggers a new `match-change` CustomEvent using the given `match` as the `detail` object.
   *
   * @ignore
   * @private
   * @param {URLMatchResult} match The match object to dispatch
   * @memberof RouterView
   */
  private triggerMatchEvent(match: URLMatchResult) {
    const eventDetail = { detail: match };
    const event = new CustomEvent('match-change', eventDetail);
    this.dispatchEvent(event);
  }
}

/**
 * Register the RouterView in the browser's `customElements` registry with the given `tagName`.
 *
 * @export
 * @param {string} [tagName='sw-router-view'] The target tag name
 * @return {*}  {void}
 */
export function define(tagName = 'sw-router-view'): void {
  defineElement(RouterView, tagName);
}
